import * as React from 'react';
import { useSelector } from 'react-redux';

// Utils
import { useTranslation } from 'react-i18next';
import { filter, pipe, prop, propEq, sortBy } from '@src/shared/src/util/general';
import { ACCOMMODATION_TYPES, ENVIRONMENT } from '@src/shared/src/const/app';
import { isEnvSearch } from '@src/util/app';
import { FARE_PAYMENT_TYPE } from '@src/util/const';
import { requireAsset } from '@toolkit/util/app';
import { formatDate } from '@src/shared/src/util/date';
// Store
import { prepayHotelRatesRemovalEnabled } from '@src/store/selectors';

// Actions, Models & Interfaces
import { HotelFareModel } from '@src/shared/src/models';
import { FareFilterType } from './HotelFareFilter';

// Components
import { HotelFareFilter } from './HotelFareFilter';
import { HotelSelection } from './HotelSelection';
import { HotelFareGroup } from './HotelFareGroup';

// Styles
import '../styles/HotelFare.scss';

type Props = {
  fares: HotelFareModel[];
  hotelRoomCount?: number;
  env: ENVIRONMENT;
  isSelectingHotel?: boolean;
  isBasketEditable?: boolean;
  accommodationType: ACCOMMODATION_TYPES;
  amenities: string[];
  onSelectHotel?: (hotelFareIds: string[]) => void;
};

type HotelFareGroupedList = {
  roomType: string;
  fares: HotelFareModel[];
};

const formatFareValue = (fare: HotelFareModel, filterKey: keyof HotelFareModel) => {
  const value = fare[filterKey];

  if (value instanceof Date) {
    return formatDate(value);
  } else if (value === undefined || value === null) {
    return '';
  } else if (filterKey === 'paymentType' && value !== FARE_PAYMENT_TYPE.PREPAY) {
    return '';
  } else {
    return value.toString();
  }
};

const getFilteredFares = (
  fares: HotelFareModel[],
  filters: FareFilterType,
): HotelFareGroupedList[] => {
  return pipe(
    sortBy(prop('sortValue')),
    // apply fare filter
    filter((fare: HotelFareModel) => {
      return Object.keys(filters).every((filterKey: keyof HotelFareModel) => {
        return filters[filterKey].some(
          (selectedOption: string) => selectedOption === formatFareValue(fare, filterKey),
        );
      });
    }),
    // group fares by room type
    (fares: HotelFareModel[]) =>
      fares.reduce((acc, fare) => {
        const existingGroup = acc.find((group) => group.roomType === fare.roomType);
        if (existingGroup) {
          existingGroup.fares.push(fare);
        } else {
          acc.push({ roomType: fare.roomType, fares: [fare] });
        }
        return acc;
      }, [] as HotelFareGroupedList[]),
  )(fares);
};

export const HotelFaresList: React.FC<Props> = (props) => {
  const { env } = props;
  const { t } = useTranslation();
  const isPrepayHotelRatesRemovalEnabled = useSelector(prepayHotelRatesRemovalEnabled);

  const uniqueRoomTypes = Array.from(new Set(props.fares.map((fare) => fare.roomType)));
  const uniqueRefundableUntil = Array.from(
    new Set(props.fares.map((fare) => fare.refundableUntil?.toDateString())),
  );

  const breakfastOptions = [
    {
      value: 'false',
      label: t('hotelFare.select.breakfast.no'),
    },
    {
      value: 'true',
      label: t('hotelFare.select.breakfast.yes'),
    },
  ];

  const roomTypeOptions = uniqueRoomTypes.map((type: string) => ({
    value: type,
    label: type,
  }));

  const refundableUntilOptions = uniqueRefundableUntil
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
    // reverse so that not cancellable comes first
    .reverse()
    .map((condition: string) =>
      condition
        ? {
            value: formatDate(new Date(condition)),
            label: `${t('hotelFare.tbody.until')} ${formatDate(new Date(condition))}`,
          }
        : {
            value: '',
            label: t('hotelFare.tbody.notCancellable'),
          },
    );

  const paymentTypeOptions = [
    {
      value: '',
      label: t('hotelFare.select.paymentType.notPrepaid'),
    },
    {
      value: FARE_PAYMENT_TYPE.PREPAY,
      label: t('hotelFare.select.paymentType.prepaid'),
    },
  ];

  const [filters, setFilters] = React.useState<FareFilterType>({
    breakfast: breakfastOptions.map((option) => option.value),
    roomType: roomTypeOptions.map((option) => option.value),
    refundableUntil: refundableUntilOptions.map((option) => option.value),
    paymentType: paymentTypeOptions.map((option) => option.value),
  });

  const fares = isEnvSearch(env) ? props.fares : filter(propEq('selected', true))(props.fares);
  const filteredFares = getFilteredFares(fares, filters);

  return (
    <div>
      <HotelSelection />
      {isEnvSearch(env) && (
        <HotelFareFilter
          selected={filters}
          onChange={setFilters}
          disabled={
            props.accommodationType === ACCOMMODATION_TYPES.VACATION_RENTAL
              ? ['breakfast', 'roomType', 'refundableUntil', 'paymentType']
              : isPrepayHotelRatesRemovalEnabled
              ? ['paymentType']
              : []
          }
          options={{
            breakfast: breakfastOptions,
            roomType: roomTypeOptions,
            refundableUntil: refundableUntilOptions,
            paymentType: paymentTypeOptions,
          }}
        />
      )}
      {filteredFares.length > 0 ? (
        filteredFares.map((group) => (
          <HotelFareGroup
            key={group.roomType}
            roomType={group.roomType}
            fares={group.fares}
            hotelRoomCount={props.hotelRoomCount}
            amenities={props.amenities}
            env={props.env}
            isSelectingHotel={props.isSelectingHotel}
            isBasketEditable={props.isBasketEditable}
            accommodationType={props.accommodationType}
            onSelectHotel={props.onSelectHotel}
          />
        ))
      ) : (
        <div className="tcp-hotel-fare-empty">
          <img
            src={requireAsset('backgrounds/no_hotel_fares.svg')}
            alt={t('hotelFare.emptyTitle')}
          />
          <div className="tcp-hotel-fare-empty-inner">
            <div className="tcp-hotel-fare-empty-title">{t('hotelFare.emptyTitle')}</div>
            <div className="tcp-hotel-fare-empty-text">{t('hotelFare.emptyText')}</div>
          </div>
        </div>
      )}
    </div>
  );
};
